import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { update_password } from '../../reduxData/user/userAction';
import { collection, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useTranslation } from 'react-i18next';

const ChangePassword = () => {
    const { t } = useTranslation();
    const { userPermissions } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const [showPass, setshowPass] = useState(false);
    const [newPass, setnewPass] = useState(false);
    const [confirmPass, setconfirmPass] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState("weak");
    const [formdata, setFormdata] = useState({
        password: '',
        newpassword: '',
        confirmpassword: ''
    });

    const [errors, setErrors] = useState({
        password: '',
        newpassword: '',
        confirmpassword: ''
    });


    // check password strength validation 
    const checkPasswordStrength = (password) => {
        const hasUpper = /[A-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        if (password.length >= 14 && hasUpper && hasNumber && hasSymbol) {
            setPasswordStrength("strong");
        } else if (password.length >= 12 && ((hasUpper && hasNumber) || hasSymbol)) {
            setPasswordStrength("medium");
        } else {
            setPasswordStrength("weak");
        }
    };

    // handle password fields value change and validations 
    const handleChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case 'password':
                setErrors({
                    ...errors,
                    password: value === '' ? t("changePassword.currentPasswordRequired") : ''
                });
                setFormdata({ ...formdata, password: value.replace(/\s/g, "") });
                break;
            case 'newpassword':
                const strongRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{14,}$/;
                setErrors({
                    ...errors,
                    newpassword: value === '' ? t("changePassword.newPasswordRequired")
                        : !strongRegex.test(value)
                            ? t("changePassword.newPasswordStrengthError") : ''
                });
                setFormdata({ ...formdata, newpassword: value.replace(/\s/g, "") });
                break;
            case 'confirmpassword':
                setErrors({
                    ...errors,
                    confirmpassword: value === '' ? t("changePassword.confirmPasswordRequired") :
                        value !== formdata?.newpassword ? t("changePassword.confirmPasswordMismatch") : ''
                });
                setFormdata({ ...formdata, confirmpassword: value.replace(/\s/g, "") });
                break;
            default:
                break;
        }
    };


    const updateAllAdminFlags = async () => {
        try {
            const docsRef = collection(db, "admins");
            const snapshot = await getDocs(docsRef);
            const batch = writeBatch(db);
            snapshot.forEach((d) => {
                batch.update(d.ref, { passwordUpdate: true });
            });
            await batch.commit();
        } catch (error) {
            console.error("Error updating admins:", error);
        }
    };

    // handle password change form submit 
    const handleSubmit = async (e) => {
        e.preventDefault();
        const { password, newpassword, confirmpassword } = formdata;
        const strongRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{14,}$/;
        const tempErrors = {
            password: !password ? t("changePassword.passwordRequired") : "",
            newpassword: !newpassword ? t("changePassword.newPasswordRequired")
                : newpassword === password ? t("changePassword.newPasswordSameAsCurrent")
                    : !strongRegex.test(newpassword) ? t("changePassword.newPasswordStrengthError")
                        : "",
            confirmpassword: !confirmpassword ? t("changePassword.confirmPasswordRequired")
                : confirmpassword !== newpassword ? t("changePassword.confirmPasswordMismatch")
                    : "",
        };
        setErrors(tempErrors);
        const hasNoErrors = Object.values(tempErrors).every((err) => err === "");
        if (!hasNoErrors) return;
        const userdata = {
            old_password: password,
            new_password: newpassword,
        };
        const passupdated = await update_password(userdata, dispatch);
        if (passupdated) {
            await updateAllAdminFlags();
            setFormdata({ password: "", newpassword: "", confirmpassword: "" });
        }
    };

    const strengthLabelKey = passwordStrength === "weak" ? "changePassword.strengthWeak"
            : passwordStrength === "medium" ? "changePassword.strengthMedium"
                : "changePassword.strengthStrong";

    return (
        <div className="change-password-container">
            <div className="change-password-card">
                <h2 className="change-password-title">{t("changePassword.title")}</h2>
                <form onSubmit={handleSubmit} className="change-password-form">
                    <div className="form-group position-relative">
                        <label htmlFor="password" className="form-label">
                            {t("changePassword.currentPasswordLabel")} <span className="requiredstar">*</span>
                        </label>
                        <div className="input-wrapper">
                            <input
                                type={showPass ? "text" : "password"}
                                name="password"
                                id="password"
                                className={`form-input ${errors.password ? 'input-error' : ''}`}
                                placeholder={t("changePassword.currentPasswordPlaceholder")}
                                value={formdata.password}
                                onChange={handleChange}
                                onKeyDown={(e) => e.key === " " && e.preventDefault()}
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setshowPass(!showPass)}
                                aria-label={t("changePassword.togglePasswordVisibility")}
                            >
                                {!showPass ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <div className="error-message">{errors.password}</div>
                        )}
                    </div>

                    <div className="form-group position-relative">
                        <label htmlFor="newpassword" className="form-label">
                           {t("changePassword.newPasswordLabel")} <span className="requiredstar">*</span>
                            <i
                                className="bi bi-info-circle"
                                style={{ marginLeft: "15px", cursor: "pointer" }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={t("changePassword.newPasswordTooltip")}
                            ></i>
                        </label>
                        <div className="input-wrapper">
                            <input
                                type={newPass ? "text" : "password"}
                                name="newpassword"
                                id="newpassword"
                                className={`form-input ${errors.newpassword ? 'input-error' : ''}`}
                                placeholder={t("changePassword.newPasswordPlaceholder")}
                                value={formdata.newpassword}
                                onChange={(e) => {
                                    handleChange(e);
                                    checkPasswordStrength(e.target.value);
                                }}
                                onKeyDown={(e) => e.key === " " && e.preventDefault()}
                                autoComplete="new-password"
                            />

                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setnewPass(!newPass)}
                                aria-label={t("changePassword.togglePasswordVisibility")}
                            >
                                {!newPass ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {formdata.newpassword && (
                            <div className="password-strength mt-2">
                                <div
                                    className={`strength-bar ${passwordStrength}`}
                                    style={{
                                        height: "6px",
                                        borderRadius: "4px",
                                        background:
                                            passwordStrength === "weak"
                                                ? "red"
                                                : passwordStrength === "medium"
                                                    ? "orange"
                                                    : "green",
                                        width:
                                            passwordStrength === "weak"
                                                ? "33%"
                                                : passwordStrength === "medium"
                                                    ? "66%"
                                                    : "100%",
                                        transition: "width 0.3s ease",
                                    }}
                                ></div>
                                <small className="text-muted mt-1 d-block">
                                    {t("changePassword.strengthLabel")}
                                    <span
                                        style={{
                                            color:
                                                passwordStrength === "weak"
                                                    ? "red"
                                                    : passwordStrength === "medium"
                                                        ? "orange"
                                                        : "green",
                                        }}>
                                            {t(strengthLabelKey)}
                                    </span>
                                </small>
                            </div>
                        )}
                        {errors.newpassword && (
                            <div className="error-message">{errors.newpassword}</div>
                        )}
                    </div>

                    <div className="form-group position-relative">
                        <label htmlFor="confirmpassword" className="form-label">
                            {t("changePassword.confirmPasswordLabel")} <span className="requiredstar">*</span>
                        </label>
                        <div className="input-wrapper">
                            <input
                                type={confirmPass ? "text" : "password"}
                                name="confirmpassword"
                                id="confirmpassword"
                                className={`form-input ${errors.confirmpassword ? 'input-error' : ''}`}
                                placeholder={t("changePassword.confirmPasswordPlaceholder")}
                                value={formdata.confirmpassword}
                                onChange={handleChange}
                                onKeyDown={(e) => e.key === " " && e.preventDefault()}
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setconfirmPass(!confirmPass)}
                                aria-label={t("changePassword.togglePasswordVisibility")}
                            >
                                {!confirmPass ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.confirmpassword && (
                            <div className="error-message">{errors.confirmpassword}</div>
                        )}
                    </div>

                    {userPermissions?.change_password?.edit && (<button type="submit" className="submit-button">
                        {t("changePassword.submitButton")}
                    </button>)}
                </form>
            </div>
        </div>
    )
}

export default ChangePassword;


